/* Feb-2026 · Adults-Only age-gate modal.
 *
 * Extends the pattern of GenreContentWarningDialog with a fuller
 * agreement CHECKLIST (5 boxes, all must be checked). Used once at
 * the entrance to the Adults-Only super-shelf. Individual shelves
 * (Dark Romance, Grey Romance, Adult Fiction, Erotica) inside still
 * fire their own GenreContentWarningDialog on first visit.
 *
 * Persistence: acceptance recorded per-shelf slug ("adults-only") in
 * localStorage AND POSTed to /api/content-warning/accept for the audit
 * log the parent chat already exposes to admins.
 *
 * iter-door-open · Once every box is ticked and Accept is pressed, a
 * full-screen "doors swinging open" transition plays for ~1.1s before
 * `onAccept` fires, so the reader is visually walked through the gate
 * rather than just seeing the dialog vanish.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 4
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, ArrowLeft, ShieldAlert } from 'lucide-react';

const STORAGE_KEY = 'pw:adults-only-gate:v1';

// Feb-2026 · Door-opening transition. Plays once the reader ticks every
// box and hits Accept — a brief "doors swinging open" beat before they
// land inside Mature Audiences Only. Matches the app's dark-academia /
// oxblood-and-gold palette.
const DOOR_IMAGE_URL = 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/09352a314a2c1d2ad29e6cb44bdb793b1bd061d37c61ff4c5a09b26792505d02.jpeg';
const DOOR_TRANSITION_MS = 1150;

export function hasAcceptedAdultsOnly() {
  try { return !!window.localStorage.getItem(STORAGE_KEY); }
  catch { return false; }
}
function remember() {
  try { window.localStorage.setItem(STORAGE_KEY, '1'); }
  catch { /* private mode */ }
}

const DEFAULT_CHECKLIST = [
  'I am 18 years or older.',
  'I understand titles here portray non-consent (dub-con/non-con), captivity, kidnapping, and graphic violence as fictional themes.',
  'I understand sexual content can be extreme — up to and including scenes marked "Extreme" on the heat scale.',
  'I acknowledge these books are catharsis, not a guide to healthy real-world relationships.',
  'I voluntarily choose to view this content.',
];

export default function AdultsOnlyGate({ open, checklist, onAccept, onRetreat }) {
  const { t } = useTranslation();
  const items = Array.isArray(checklist) && checklist.length > 0 ? checklist : DEFAULT_CHECKLIST;
  const [checked, setChecked] = React.useState(() => items.map(() => false));
  // Feb-2026 · Door-opening transition state. `opening` swaps the
  // checklist dialog for a full-screen door overlay; `ajar` is flipped
  // a tick later so the CSS transition actually animates instead of
  // starting already-open.
  const [opening, setOpening] = React.useState(false);
  const [ajar, setAjar] = React.useState(false);
  // iter-door-open-fix — Radix's AlertDialogAction auto-closes the
  // dialog synchronously on click (firing `onOpenChange(false)`)
  // *before* React re-renders with the `opening` state set above, so
  // `onOpenChange` would still see the stale `opening === false` and
  // wrongly call `onRetreat`. A ref updates immediately, with no
  // render in between, so it's the only reliable guard here.
  const openingRef = React.useRef(false);
  React.useEffect(() => {
    if (open) { setChecked(items.map(() => false)); setOpening(false); setAjar(false); openingRef.current = false; }
  }, [open, items]);

  // Feb-2026 · Warm the browser cache for the door image the moment the
  // checklist opens — ticking 5 boxes takes a few seconds, which is
  // normally enough lead time for the image to finish loading before
  // Accept triggers the animation.
  React.useEffect(() => {
    if (open) { const img = new Image(); img.src = DOOR_IMAGE_URL; }
  }, [open]);

  const allChecked = checked.length > 0 && checked.every(Boolean);

  const handleAccept = async () => {
    if (!allChecked) return;
    // iter-door-open-fix — Set this synchronously, as the very first
    // statement, before the `await fetch(...)` below yields control.
    // Radix's AlertDialogAction closes the dialog (and fires
    // `onOpenChange(false)`) synchronously on click, in the same tick
    // — if this ref isn't already true by then, the guard below can't
    // catch it and `onRetreat` fires wrongly before the door even opens.
    openingRef.current = true;
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      await fetch(`${API}/content-warning/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: 'adults-only',
          confirmed_18: true,
          source: 'adults-only-gate',
          checklist_items_acknowledged: items.length,
        }),
      });
    } catch { /* silent */ }
    remember();
    setOpening(true);
    // One frame so the closed-door image paints before the leaves swing.
    window.requestAnimationFrame(() => {
      window.setTimeout(() => setAjar(true), 50);
    });
    window.setTimeout(() => {
      onAccept?.();
      // Reset so the overlay doesn't linger for callers that stay on
      // the same page (e.g. a deep-linked genre page) instead of
      // navigating away once the gate is passed.
      setOpening(false);
      setAjar(false);
    }, DOOR_TRANSITION_MS);
  };

  return (
    <>
      <AlertDialog open={open && !opening} onOpenChange={(next) => { if (!next && !openingRef.current) onRetreat?.(); }}>
        <AlertDialogContent
          data-testid="adults-only-gate"
          className="flex max-h-[calc(100dvh-2rem)] max-w-xl flex-col overscroll-contain [&_*]:min-w-0 [overflow-wrap:anywhere]"
        >
          <AlertDialogHeader className="shrink-0">
            <AlertDialogTitle className="flex items-center gap-2 text-destructive dark:text-destructive/90">
              <ShieldAlert className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1">Mature Audiences Only · 18+ shelves</span>
            </AlertDialogTitle>
          </AlertDialogHeader>

          {/* Scrollable body — header description + checklist */}
          <div className="flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
            <AlertDialogDescription className="space-y-2 text-[13px] leading-relaxed">
              <span className="block">
                You&rsquo;re entering the gated <strong>Mature Audiences Only</strong> section — home
                to Dark Romance, Grey Romance, Adult Fiction, and Erotica. Titles here routinely portray
                <strong> non-consent, captivity, graphic violence,</strong> and
                <strong> extreme sexual content</strong> as fictional catharsis.
              </span>
              <span className="block rounded-sm border border-destructive/40 bg-destructive/10 p-2 text-[12px]">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                Each shelf inside will also show its own trigger warning the first
                time you open it. Individual titles list their triggers on hover
                — kidnapping, non-consent, violence-against-protagonist, and
                extreme-heat markers are always displayed on the card.
              </span>
            </AlertDialogDescription>

            <div
              className="mt-3 space-y-1.5 rounded-sm border border-destructive/40 bg-destructive/5 p-2.5"
              data-testid="adults-only-gate-checklist"
            >
              {items.map((label, i) => (
                <label
                  key={i}
                  className="flex cursor-pointer select-none items-start gap-2 text-[12.5px] leading-snug"
                  data-testid={`adults-only-gate-item-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={checked[i] || false}
                    onChange={(e) => {
                      const next = [...checked];
                      next[i] = e.target.checked;
                      setChecked(next);
                    }}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-destructive"
                  />
                  <span className="min-w-0 flex-1">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Sticky footer — always visible, even when dialog body scrolls */}
          <AlertDialogFooter className="shrink-0 border-t border-border/40 pt-3 flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2">
            <AlertDialogCancel
              onClick={onRetreat}
              data-testid="adults-only-gate-retreat"
              className="inline-flex min-w-0 items-center gap-1.5"
            >
              <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t('bookstore.adultsOnlyDecline')}</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAccept}
              disabled={!allChecked}
              data-testid="adults-only-gate-accept"
              className="min-w-0 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="truncate">
                {allChecked ? t('bookstore.adultsOnlyConfirm') : t('bookstore.adultsOnlyTickAll', { count: items.length })}
              </span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {opening && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black"
          data-testid="adults-only-door-transition"
        >
          <div className="relative h-full w-full max-w-md overflow-hidden" style={{ perspective: '1600px' }}>
            {/* Left leaf — shows the left half of the door image. */}
            <div
              className="absolute inset-y-0 left-0 w-1/2 shadow-[8px_0_30px_rgba(0,0,0,0.6)] transition-transform ease-[cubic-bezier(.65,0,.35,1)]"
              style={{
                backgroundImage: `url(${DOOR_IMAGE_URL})`,
                backgroundSize: '200% 100%',
                backgroundPosition: 'left center',
                transformOrigin: 'left center',
                transform: ajar ? 'rotateY(-78deg) translateX(-2%)' : 'rotateY(0deg)',
                transitionDuration: `${DOOR_TRANSITION_MS - 150}ms`,
              }}
            />
            {/* Right leaf — shows the right half of the door image. */}
            <div
              className="absolute inset-y-0 right-0 w-1/2 shadow-[-8px_0_30px_rgba(0,0,0,0.6)] transition-transform ease-[cubic-bezier(.65,0,.35,1)]"
              style={{
                backgroundImage: `url(${DOOR_IMAGE_URL})`,
                backgroundSize: '200% 100%',
                backgroundPosition: 'right center',
                transformOrigin: 'right center',
                transform: ajar ? 'rotateY(78deg) translateX(2%)' : 'rotateY(0deg)',
                transitionDuration: `${DOOR_TRANSITION_MS - 150}ms`,
              }}
            />
            {/* Warm light spilling through as the doors part. */}
            <div
              className="absolute inset-0 transition-opacity"
              style={{
                background: 'radial-gradient(circle, rgba(255,214,150,0.9) 0%, rgba(74,5,17,0.4) 45%, transparent 75%)',
                opacity: ajar ? 1 : 0,
                transitionDuration: `${DOOR_TRANSITION_MS - 300}ms`,
              }}
            />
          </div>
          <div
            className="pointer-events-none absolute bottom-10 left-1/2 -translate-x-1/2 text-center text-[11px] uppercase tracking-[0.32em] text-white/80 transition-opacity"
            style={{ opacity: ajar ? 0 : 1, fontFamily: 'inherit' }}
            data-testid="adults-only-door-transition-label"
          >
            Entering Mature Audiences Only…
          </div>
        </div>
      )}
    </>
  );
}
